import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Info } from "lucide-react";
import { api } from "@/lib/api";
import { NOTIFY } from "@/constants/testIds";

export default function Notifications() {
    const [logs, setLogs] = useState([]);
    const [email, setEmail] = useState({ to: "", subject: "", html: "" });
    const [sms, setSms] = useState({ to: "", body: "" });

    async function load() {
        try {
            const { data } = await api.get("/notify/logs");
            setLogs(data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    async function sendEmail(e) {
        e.preventDefault();
        try {
            const { data } = await api.post("/notify/email", email);
            toast.success(data.mocked ? "Email logged (Resend not configured)" : "Email sent");
            setEmail({ to: "", subject: "", html: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        }
    }

    async function sendSms(e) {
        e.preventDefault();
        try {
            const { data } = await api.post("/notify/sms", sms);
            toast.success(data.mocked ? "SMS logged (Twilio not configured)" : "SMS sent");
            setSms({ to: "", body: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        }
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid={NOTIFY.root} className="max-w-6xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Notification hooks</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        Email (Resend) + SMS (Twilio)
                    </h1>
                    <p className="text-sm text-stone-500 mt-2 font-dmsans flex items-center gap-1">
                        <Info className="w-3.5 h-3.5" />
                        Providers auto-fallback to mocked mode until keys are pasted in the env. All sends are logged either way.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                                <Mail className="w-5 h-5 text-emerald-700" /> Send email
                            </h3>
                            <form onSubmit={sendEmail} className="space-y-3">
                                <Input
                                    data-testid={NOTIFY.emailToInput}
                                    placeholder="Recipient email"
                                    type="email"
                                    value={email.to}
                                    onChange={(e) => setEmail({ ...email, to: e.target.value })}
                                    required
                                    className="h-11 rounded-xl"
                                />
                                <Input
                                    data-testid={NOTIFY.emailSubjectInput}
                                    placeholder="Subject"
                                    value={email.subject}
                                    onChange={(e) => setEmail({ ...email, subject: e.target.value })}
                                    required
                                    className="h-11 rounded-xl"
                                />
                                <Textarea
                                    data-testid={NOTIFY.emailBodyInput}
                                    placeholder="Body (HTML supported)"
                                    value={email.html}
                                    onChange={(e) => setEmail({ ...email, html: e.target.value })}
                                    required
                                    className="rounded-xl min-h-[120px]"
                                />
                                <Button
                                    data-testid={NOTIFY.emailSendBtn}
                                    type="submit"
                                    className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-11"
                                >
                                    <Send className="w-4 h-4 mr-2" /> Send email
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-orange-600" /> Send SMS
                            </h3>
                            <form onSubmit={sendSms} className="space-y-3">
                                <Input
                                    data-testid={NOTIFY.smsToInput}
                                    placeholder="Recipient phone (+15550100)"
                                    value={sms.to}
                                    onChange={(e) => setSms({ ...sms, to: e.target.value })}
                                    required
                                    className="h-11 rounded-xl"
                                />
                                <Textarea
                                    data-testid={NOTIFY.smsBodyInput}
                                    placeholder="Message"
                                    value={sms.body}
                                    onChange={(e) => setSms({ ...sms, body: e.target.value })}
                                    required
                                    className="rounded-xl min-h-[120px]"
                                />
                                <Button
                                    data-testid={NOTIFY.smsSendBtn}
                                    type="submit"
                                    className="rounded-full bg-orange-600 hover:bg-orange-700 text-white h-11"
                                >
                                    <Send className="w-4 h-4 mr-2" /> Send SMS
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>

                <Card className="rounded-2xl border-stone-200">
                    <CardContent className="p-6">
                        <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4">Delivery log</h3>
                        <div data-testid={NOTIFY.logs} className="space-y-2 max-h-[500px] overflow-y-auto">
                            {logs.length === 0 && (
                                <p className="text-sm text-stone-500 font-dmsans">No notifications sent yet.</p>
                            )}
                            {logs.map((l) => (
                                <div key={l.id} className="p-4 border border-stone-200 rounded-xl hover:bg-stone-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            {l.channel === "email"
                                                ? <Mail className="w-4 h-4 text-emerald-700" />
                                                : <MessageSquare className="w-4 h-4 text-orange-600" />}
                                            <span className="font-medium text-stone-900 text-sm">{l.to}</span>
                                        </div>
                                        <Badge className={
                                            l.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50" :
                                            l.status === "mocked" ? "bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-100" :
                                            l.status === "failed" ? "bg-red-50 text-red-700 border-red-100 hover:bg-red-50" :
                                            "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-50"
                                        }>
                                            {l.status}
                                        </Badge>
                                    </div>
                                    {l.subject && <div className="text-xs text-stone-600 mt-1 font-dmsans">{l.subject}</div>}
                                    <div className="text-xs text-stone-500 mt-1 font-dmsans line-clamp-2">{l.body}</div>
                                    <div className="text-xs text-stone-400 mt-1 font-dmsans">{new Date(l.created_at).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
